import React, { useEffect, useState } from "react";
import PageHeader from "../components/PageHeader";
import { Spinner } from "react-bootstrap";
import trash from "../assets/trash.svg";
import editIcon from "../assets/edit.svg";
import { toast } from "react-toastify";
import { fetchBlogCategoryList, saveBlogCategory, updateBlogCategory, deleteBlogCategory } from "../action/formaction";
import CommonModal from "../components/CommonModal";
import MediaPickerModal from "../components/MediaPickerModal";
import cross from "../assets/black-cross.svg";
import DeleteModal from "../components/DeleteModal";

const BlogCategories = () => {
    const [categories, setCategories] = useState({ loading: false, data: [] });
    const [showModal, setShowModal] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [currentCategory, setCurrentCategory] = useState({ id: null, name: "", thumbnail: "" });
    const [showMediaPicker, setShowMediaPicker] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [categoryToDelete, setCategoryToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const fetchCategories = async () => {
        setCategories({ ...categories, loading: true });
        try {
            const result = await fetchBlogCategoryList(1, 100);
            if (result?.status) {
                setCategories({ data: result?.data?.list || [], loading: false });
            }
        } catch (error) {
            setCategories({ data: [], loading: false });
        }
    };

    useEffect(() => {
        fetchCategories();
    }, []);

    const handleSave = async () => {
        if (!currentCategory.name) {
            toast.error("Please enter category name");
            return;
        }
        setSaving(true);
        try {
            const thumbnailPath = currentCategory.thumbnail
                ? currentCategory.thumbnail.replace(process.env.REACT_APP_BUCKET_URL, "")
                : "";
            const payload = {
                name: currentCategory.name,
                thumbnail: thumbnailPath,
            };

            let res;
            if (editMode) {
                res = await updateBlogCategory(currentCategory.id, payload);
            } else {
                res = await saveBlogCategory(payload);
            }

            if (res?.status) {
                toast.success(editMode ? "Category updated!" : "Category created!");
                setShowModal(false);
                fetchCategories();
                resetForm();
            } else {
                toast.error(res?.message || "Something went wrong");
            }
        } catch (error) {
            toast.error("Error saving category");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            const res = await deleteBlogCategory(categoryToDelete);
            if (res?.status) {
                toast.success("Category deleted!");
                setShowDeleteModal(false);
                fetchCategories();
            } else {
                toast.error(res?.message || "Failed to delete");
            }
        } catch (error) {
            toast.error("Error deleting category");
        } finally {
            setDeleting(false);
        }
    };

    const resetForm = () => {
        setCurrentCategory({ id: null, name: "", thumbnail: "" });
        setEditMode(false);
    };

    const openEditModal = (cat) => {
        setEditMode(true);
        setCurrentCategory({
            id: cat._id,
            name: cat.name,
            thumbnail: (cat.thumbnail || cat.extras?.thumbnail) ? process.env.REACT_APP_BUCKET_URL + (cat.thumbnail || cat.extras.thumbnail) : "",
        });
        setShowModal(true);
    };

    return (
        <>
            <PageHeader pageTitle="Blog Categories">
                <button className="btn-black" onClick={() => { resetForm(); setShowModal(true); }}>
                    Create New
                </button>
            </PageHeader>

            <div className="page-container">
                <div className="theme-card p-4">
                    <div className="card-title pb-3">All Categories</div>
                    <div className="table-responsive">
                        <table className="pages-table">
                            <thead>
                                <tr>
                                    <th>Thumbnail</th>
                                    <th>Category Name</th>
                                    <th style={{ textAlign: "center" }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {categories.loading ? (
                                    <tr>
                                        <td colSpan="3" className="text-center py-4">
                                            <Spinner />
                                        </td>
                                    </tr>
                                ) : categories.data.length === 0 ? (
                                    <tr>
                                        <td colSpan="3" className="text-center py-4">No categories found</td>
                                    </tr>
                                ) : (
                                    categories.data.map((cat) => (
                                        <tr key={cat._id}>
                                            <td style={{ width: "80px" }}>
                                                <div style={{ width: "50px", height: "50px", borderRadius: "8px", overflow: "hidden", background: "#f5f5f5" }}>
                                                    {cat?.thumbnail || cat?.extras?.thumbnail ? (
                                                        <img
                                                            src={process.env.REACT_APP_BUCKET_URL + (cat.thumbnail || cat.extras.thumbnail)}
                                                            alt=""
                                                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                                        />
                                                    ) : (
                                                        <div className="d-flex align-items-center justify-content-center h-100" style={{ color: "#ccc", fontSize: "10px" }}>No Img</div>
                                                    )}
                                                </div>
                                            </td>
                                            <td>{cat.name}</td>
                                            <td style={{ textAlign: "center" }}>
                                                <div className="d-flex justify-content-center gap-2">
                                                    <button className="edit-btn" onClick={() => openEditModal(cat)}>
                                                        <img src={editIcon} alt="edit" style={{ width: "18px" }} />
                                                    </button>
                                                    <button className="delete-btn" onClick={() => { setCategoryToDelete(cat._id); setShowDeleteModal(true); }}>
                                                        <img src={trash} alt="delete" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <CommonModal show={showModal} onClose={() => setShowModal(false)}>
                <div className="p-3">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h5 className="mb-0">{editMode ? "Edit Category" : "Add New Category"}</h5>
                        <img src={cross} alt="close" className="pointer" onClick={() => setShowModal(false)} />
                    </div>

                    <div className="frm-group">
                        <label className="frm-label">Category Name</label>
                        <input
                            type="text"
                            className="frm-input"
                            placeholder="Enter category name"
                            value={currentCategory.name}
                            onChange={(e) => setCurrentCategory({ ...currentCategory, name: e.target.value })}
                        />
                    </div>

                    <div className="frm-group">
                        <label className="frm-label">Category Thumbnail</label>
                        <div
                            className="pointer d-flex align-items-center justify-content-center"
                            style={{
                                width: "100%",
                                height: "150px",
                                borderRadius: "12px",
                                background: "#f5f5f5",
                                overflow: "hidden",
                                border: "1px dashed #ccc",
                            }}
                            onClick={() => setShowMediaPicker(true)}
                        >
                            {currentCategory.thumbnail ? (
                                <img
                                    src={currentCategory.thumbnail}
                                    alt="thumbnail"
                                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                />
                            ) : (
                                <span style={{ color: "#777" }}>Upload Thumbnail</span>
                            )}
                        </div>
                    </div>

                    <div className="mt-4 d-flex gap-2">
                        <button className="btn btn-white w-100" onClick={() => setShowModal(false)}>Cancel</button>
                        <button className="btn btn-black w-100" onClick={handleSave} disabled={saving}>
                            {saving ? <Spinner size="sm" /> : (editMode ? "Update Category" : "Save Category")}
                        </button>
                    </div>
                </div>
            </CommonModal>

            {showMediaPicker && (
                <MediaPickerModal
                    show={showMediaPicker}
                    onClose={() => setShowMediaPicker(false)}
                    onSelect={(url) => {
                        setCurrentCategory({ ...currentCategory, thumbnail: url });
                        setShowMediaPicker(false);
                    }}
                />
            )}

            <DeleteModal
                show={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onDelete={handleDelete}
                deleting={deleting}
            />
        </>
    );
};

export default BlogCategories;
